/**
 * /settings/api-keys -- tenant-scoped API key management.
 *
 * Backend already lives under /api/admin/api-keys (GET / POST / DELETE).
 * This is the missing UI: list active keys, create one (secret returned ONCE),
 * revoke. Same secret-reveal pattern as the webhooks page.
 */
import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { KeyRound, Copy, Check, AlertTriangle, Trash2, Plus } from 'lucide-react';
import { api, extractApiError } from '@/lib/api';
import { toast } from '@/stores/toast';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';

interface ApiKey {
  id: string;
  name: string;
  prefix: string;
  isActive: boolean;
  lastUsedAt?: string | null;
  expiresAt?: string | null;
  createdAt: string;
}

interface CreatedKey extends ApiKey {
  key: string;
}

export function SettingsApiKeysPage() {
  const qc = useQueryClient();
  const keys = useQuery({
    queryKey: ['api-keys'],
    queryFn: async () => (await api.get<{ items: ApiKey[] }>('/api/admin/api-keys')).data.items,
  });

  const [name, setName] = useState('');
  const [expires, setExpires] = useState<string>('365');
  const [revealed, setRevealed] = useState<{ id: string; key: string } | null>(null);

  const create = useMutation({
    mutationFn: async () => {
      const res = await api.post<CreatedKey>('/api/admin/api-keys', {
        name,
        expiresInDays: expires === 'never' ? undefined : Number(expires),
      });
      return res.data;
    },
    onSuccess: (data) => {
      toast.success('API key created', 'Copy now -- not shown again.');
      setRevealed({ id: data.id, key: data.key });
      setName('');
      qc.invalidateQueries({ queryKey: ['api-keys'] });
    },
    onError: (err) => toast.error('Create failed', extractApiError(err).message),
  });

  const revoke = useMutation({
    mutationFn: async (id: string) => api.delete(`/api/admin/api-keys/${id}`),
    onSuccess: () => {
      toast.success('Key revoked');
      qc.invalidateQueries({ queryKey: ['api-keys'] });
    },
    onError: (err) => toast.error('Revoke failed', extractApiError(err).message),
  });

  const active = keys.data?.filter((k) => k.isActive) ?? [];
  const revoked = keys.data?.filter((k) => !k.isActive) ?? [];

  return (
    <div className="space-y-6">
      <header>
        <h1 className="font-display text-2xl">API keys</h1>
        <p className="text-sm text-muted-foreground">
          Use API keys for server-to-server calls (Praxara MCP, custom integrations, ETL). Each key
          is shown once on creation; if you lose it, revoke and create a new one.
        </p>
      </header>

      {revealed && (
        <KeyRevealCard secret={revealed.key} onDismiss={() => setRevealed(null)} />
      )}

      <Card>
        <CardHeader>
          <CardTitle><span className="inline-flex items-center gap-2"><KeyRound className="h-5 w-5" /> New API key</span></CardTitle>
          <CardDescription>
            Use Authorization: <code>Bearer &lt;your-key&gt;</code>. Treat like a password.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="grid gap-3 md:grid-cols-2">
            <div>
              <Label>Name</Label>
              <Input placeholder="e.g. Production ETL" value={name} onChange={(e) => setName(e.target.value)} />
            </div>
            <div>
              <Label>Expiration</Label>
              <select
                value={expires}
                onChange={(e) => setExpires(e.target.value)}
                className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
              >
                <option value="30">30 days</option>
                <option value="90">90 days</option>
                <option value="180">180 days</option>
                <option value="365">365 days</option>
                <option value="never">Never expire</option>
              </select>
            </div>
          </div>
          <div>
            <Button onClick={() => create.mutate()} disabled={create.isPending || !name}>
              <Plus className="mr-1 h-3.5 w-3.5" /> {create.isPending ? 'Creating...' : 'Create key'}
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Active keys</CardTitle>
          <CardDescription>{active.length} active.</CardDescription>
        </CardHeader>
        <CardContent>
          {keys.isLoading && <div className="text-sm text-muted-foreground">Loading...</div>}
          {!keys.isLoading && active.length === 0 && (
            <div className="rounded-md border border-dashed p-6 text-center text-sm text-muted-foreground">
              No active API keys. Create one above.
            </div>
          )}
          <div className="divide-y">
            {active.map((k) => (
              <KeyRow key={k.id} apiKey={k} onRevoke={() => revoke.mutate(k.id)} />
            ))}
          </div>
        </CardContent>
      </Card>

      {revoked.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Revoked keys</CardTitle>
            <CardDescription>For audit; cannot be reactivated.</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="divide-y">
              {revoked.map((k) => (
                <KeyRow key={k.id} apiKey={k} onRevoke={() => undefined} disabled />
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function KeyRevealCard({ secret, onDismiss }: { secret: string; onDismiss: () => void }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(secret);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // ignore
    }
  };
  return (
    <Card className="border-amber-200 bg-amber-50 dark:border-amber-900 dark:bg-amber-950/30">
      <CardContent className="space-y-2 p-4">
        <div className="flex items-center gap-2 text-sm font-medium text-amber-900 dark:text-amber-200">
          <AlertTriangle className="h-4 w-4" /> Copy this key now -- it will not be shown again.
        </div>
        <div className="flex items-center gap-2">
          <code className="flex-1 truncate rounded border bg-background px-2 py-1.5 font-mono text-xs">{secret}</code>
          <Button size="sm" variant="outline" onClick={copy}>
            {copied ? <Check className="mr-1 h-3.5 w-3.5" /> : <Copy className="mr-1 h-3.5 w-3.5" />} {copied ? 'Copied' : 'Copy'}
          </Button>
          <Button size="sm" variant="ghost" onClick={onDismiss}>Dismiss</Button>
        </div>
      </CardContent>
    </Card>
  );
}

function KeyRow({ apiKey, onRevoke, disabled }: { apiKey: ApiKey; onRevoke: () => void; disabled?: boolean }) {
  const expiresLabel = apiKey.expiresAt
    ? new Date(apiKey.expiresAt) < new Date()
      ? 'Expired'
      : `Expires ${new Date(apiKey.expiresAt).toLocaleDateString()}`
    : 'Never expires';
  const lastUsed = apiKey.lastUsedAt
    ? `Last used ${new Date(apiKey.lastUsedAt).toLocaleDateString()}`
    : 'Never used';
  return (
    <div className="flex items-center justify-between gap-3 py-3">
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className="truncate font-medium">{apiKey.name}</span>
          {!apiKey.isActive && <Badge variant="secondary">Revoked</Badge>}
        </div>
        <div className="mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs text-muted-foreground">
          <code className="font-mono">{apiKey.prefix}***</code>
          <span>{lastUsed}</span>
          <span>{expiresLabel}</span>
          <span>Created {new Date(apiKey.createdAt).toLocaleDateString()}</span>
        </div>
      </div>
      {!disabled && (
        <Button
          size="sm"
          variant="ghost"
          className="text-destructive hover:text-destructive"
          onClick={() => {
            if (window.confirm(`Revoke key "${apiKey.name}"? This cannot be undone.`)) onRevoke();
          }}
        >
          <Trash2 className="mr-1 h-3.5 w-3.5" /> Revoke
        </Button>
      )}
    </div>
  );
}
